import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Camera, ScanLine, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSocket from "@/hooks/useSocket";
import { sound } from "@/lib/sound";
import BillCard from "@/components/BillCard";
import { Skeleton } from "@/components/ui/skeleton";

export default function Dashboard() {
  const { user } = useAuth();
  const [bills, setBills] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/bills");
      setBills(r.data.bills || []);
    } catch {
      setBills([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);

  useSocket(user?.id, () => load());

  const active = (bills || []).filter((b) =>
    ["collecting", "partially_funded"].includes(b.status)
  );

  return (
    <div className="space-y-7">
      {/* Hero */}
      <div className="pt-1">
        <p className="text-[26px] font-bold text-white/90 leading-tight">Split the bill.</p>
        <h1 className="text-[52px] leading-[0.98] font-extrabold tracking-tight">
          <span className="text-white">Pay in </span>
          <span className="text-[#a855f7]">seconds.</span>
        </h1>
        <p className="mt-3 text-[16px] leading-snug text-muted-foreground max-w-[20rem]">
          Scan the receipt, everyone claims what they had, and pays you back on the spot.
        </p>
      </div>

      {/* Two-choice launcher */}
      <div className="space-y-3">
        <Link
          to="/create"
          data-testid="launch-start-bill"
          onClick={() => sound.play("nav")}
          className="hp-tile-primary hp-card-hover rounded-3xl p-5 flex items-center gap-4 text-white"
        >
          <div className="h-14 w-14 rounded-2xl grid place-items-center bg-white/15 shrink-0">
            <Camera size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[20px] font-extrabold leading-tight">Start a bill</p>
            <p className="text-[14px] text-white/75 mt-0.5">
              Scan a receipt or enter a total — you're the host
            </p>
          </div>
          <ChevronRight size={22} className="shrink-0" />
        </Link>

        <Link
          to="/join"
          data-testid="launch-pay-bill"
          onClick={() => sound.play("nav")}
          className="hp-glass hp-card-hover rounded-3xl p-5 flex items-center gap-4 text-white"
        >
          <div className="h-14 w-14 rounded-2xl grid place-items-center bg-[#7c3aed]/25 text-[#c084fc] shrink-0">
            <ScanLine size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[20px] font-extrabold leading-tight">Pay a bill</p>
            <p className="text-[14px] text-muted-foreground mt-0.5">
              Scan the host's QR or enter the invite code
            </p>
          </div>
          <ChevronRight size={22} className="text-muted-foreground shrink-0" />
        </Link>
      </div>

      {/* Active bills to resume */}
      {(bills === null || active.length > 0) && (
        <section>
          <h2 className="text-[20px] font-extrabold text-white mb-3">Happening now</h2>
          {bills === null ? (
            <Skeleton className="h-40 rounded-3xl bg-white/5" />
          ) : (
            <div className="space-y-3">
              {active.map((b, i) => (
                <BillCard key={b.id} bill={b} index={i} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
